<template>
  <v-dialog v-model="isVisible" width="unset">
    <v-card>
      <v-card-text class="pa-0 px-4">
        <css-spinner
          ><span class="text-secondary"
            >Adding {{ Number(totalFeatures).toLocaleString()
            }}{{ totalFeatures === 1 ? ` feature` : ` features`
            }}{{ fileName ? ` from file ` : `` }}</span
          >{{ fileName ? `${fileName}...` : `...` }}</css-spinner
        >
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import CssSpinner from "@/components/shared/CssSpinner";
import mapUtils from "@/utils/map";
import turfBbox from "@turf/bbox";
import {
  getFeatureCollectionsLength,
  categorizeFeaturesInCollection,
} from "./FeatureProcessorApi.js";
import { createNamespacedHelpers } from "vuex-composition-helpers";
const uiStore = createNamespacedHelpers("ui");

export default {
  components: { CssSpinner },
  mixins: [mapUtils],
  props: {
    featureCollections: {
      type: Array,
      required: true,
    },
    map: {
      type: Object,
      required: true,
    },
    limits: {
      type: Object,
      required: true,
    },
    fileName: {
      type: String,
      required: false,
    },
  },
  data() {
    return {
      isVisible: false,
      addedFeatures: [],
      validFeatures: [],
      errors: [],
    };
  },
  computed: {
    totalFeatures() {
      let total = 0;

      if (this.featureCollections && Array.isArray(this.featureCollections)) {
        this.featureCollections.forEach((featureCollection) => {
          if (featureCollection && Array.isArray(featureCollection.features)) {
            total += featureCollection.features.length;
          }
        }, this);
      }

      return total;
    },
  },
  setup() {
    const { SHOW_SNACKBAR } = uiStore.useMutations(["SHOW_SNACKBAR"]);
    return { SHOW_SNACKBAR };
  },
  methods: {
    cancel() {
      this.isVisible = false;
    },
    async processFeatureCollections() {
      this.isVisible = true;
      const fileName = this.fileName;

      // first check if the character length of all the features in all of the feature collections is too long
      let featureCollectionsCharacterLength = await getFeatureCollectionsLength(
        this.featureCollections
      );

      if (
        featureCollectionsCharacterLength > this.limits.MAX_AOI_STRING_LENGTH
      ) {
        this.SHOW_SNACKBAR({
          text: `Unable to add AOI's.  ${
            fileName ? `The file "${fileName}" is ` : "The AOI's are"
          } too long with ${Number(
            featureCollectionsCharacterLength
          ).toLocaleString()} characters.  Up to ${Number(
            this.limits.MAX_AOI_STRING_LENGTH
          ).toLocaleString()} characters are supported.`,
          color: "error",
        });

        this.cancel();
        this.$emit("done");
        return;
      }

      this.addedFeatures = [];
      this.validFeatures = [];
      this.errors = [];

      for (let i = 0; i < this.featureCollections.length; i++) {
        const featureCollection = this.featureCollections[i];

        // start adding valid features in each feature collection
        const { valid, errors } = await categorizeFeaturesInCollection(
          featureCollection,
          this.limits
        );
        this.errors = this.errors.concat(errors);

        if (valid.length > 0) {
          this.addFeaturesToMap(valid);
        }
      }

      if (
        this.totalFeatures.length === 0 ||
        this.errors.length === this.totalFeatures.length
      ) {
        this.SHOW_SNACKBAR({
          text: null,
          html: `<p>Unable to add ${
            this.errors.length === 1
              ? "the feature"
              : `any of the ${this.errors.length} features`
          }${
            fileName ? ` uploaded from the file "${fileName}"` : ""
          } due to the following:</p>${this.getErrorList(this.errors)}`,
          color: "error",
        });
      } else if (this.errors.length > 0) {
        this.SHOW_SNACKBAR({
          text: "",
          html: `<p>${fileName ? `The file "${fileName}" had ` : ""}${
            this.errors.length
          } feature${
            this.errors.length === 1 ? "" : "s"
          } that could NOT be added due to the following:</p>${this.getErrorList(
            this.errors
          )}`,
          color: "warning",
        });
      }

      if (this.addedFeatures.length > 0) {
        this.map.setDrawMode("simple_select");

        setTimeout(() => {
          // zoom to fit the bounds of everything after things have been processed
          const bounds = turfBbox({
            type: "FeatureCollection",
            features: this.addedFeatures,
          });

          this.$log.debug("panning map to bounds: ", JSON.stringify(bounds));
          this.panMap(this.map, bounds);
          this.isVisible = false;
          this.$emit("done", this.addedFeatures);
        }, 50);
      } else {
        this.isVisible = false;
        this.$emit("done");
      }
    },
    addFeaturesToMap(features) {
      try {
        this.map.addFeatures(features);
        this.addedFeatures = this.addedFeatures.concat(features);
      } catch (err) {
        this.errors.push(`${err.toString()}`);
      }
    },
    getErrorList(errors) {
      let errorMessage = "";
      let errorObj = {};
      if (errors && Array.isArray(errors) && errors.length > 0) {
        for (let i = 0; i < errors.length; i++) {
          const e = errors[i];

          if (errorObj[e]) {
            errorObj[e].count++;
          } else {
            errorObj[e] = {
              count: 1,
            };
          }
        }

        for (const key in errorObj) {
          errorMessage += `<li>${key} (${Number(
            errorObj[key].count
          ).toLocaleString()})</li>`;
        }
      }

      return `<ul>${errorMessage}</ul>`;
    },
  },
};
</script>
